import {
  SupplierApiError,
  mapSupplierApiError,
  supplierGetAndParse,
  supplierPatchAndParse,
  supplierPostAndParse,
} from './supplierApiCore';
import {
  createSupplierFactorDraftRequestV1Schema,
  supplierFactorDraftCommandRequestV1Schema,
  supplierFactorDraftListResponseV1Schema,
  supplierFactorDraftResponseV1Schema,
  updateSupplierFactorDraftRequestV1Schema,
} from './supplierDraftContracts';
import { publishSupplierFactorResponseV1Schema } from './supplierFactorContracts';

export const SUPPLIER_DRAFT_ENDPOINTS = Object.freeze({
  list: '/supplier/drafts',
  create: '/supplier/drafts',
  get: (draftId) => `/supplier/drafts/${draftId}`,
  patch: (draftId) => `/supplier/drafts/${draftId}`,
  discard: (draftId) => `/supplier/drafts/${draftId}/discard`,
  confirm: (draftId) => `/supplier/drafts/${draftId}/confirm`,
});

export class SupplierDraftApiError extends SupplierApiError {
  constructor(payload, httpStatus) {
    super(payload, httpStatus, 'Supplier draft request failed');
    this.name = 'SupplierDraftApiError';
  }
}

const mapApiError = (error) => mapSupplierApiError(error, SupplierDraftApiError);

const getAndParse = (endpoint, schema) => supplierGetAndParse(endpoint, schema, mapApiError);
const postAndParse = (endpoint, body, schema) =>
  supplierPostAndParse(endpoint, body, schema, mapApiError);
const patchAndParse = (endpoint, body, schema) =>
  supplierPatchAndParse(endpoint, body, schema, mapApiError);

export const fetchSupplierDrafts = () =>
  getAndParse(SUPPLIER_DRAFT_ENDPOINTS.list, supplierFactorDraftListResponseV1Schema);

// S10 "draft-by-default": a supplier proposing a draft instead of publishing
// directly. `body` is the caller's targetSeriesKey/interval/proposedFactor —
// version is stamped here. `async` (like patchSupplierDraft below, unlike
// discard/confirm's fixed `{version:1}` body) so a client-side validation
// failure on the caller-supplied body rejects the returned promise instead
// of throwing synchronously.
export const createSupplierDraft = async (body) => {
  const payload = createSupplierFactorDraftRequestV1Schema.parse({ version: 1, ...body });
  return postAndParse(
    SUPPLIER_DRAFT_ENDPOINTS.create,
    payload,
    supplierFactorDraftResponseV1Schema,
  );
};

export const fetchSupplierDraft = (draftId) =>
  getAndParse(SUPPLIER_DRAFT_ENDPOINTS.get(draftId), supplierFactorDraftResponseV1Schema);

// Only legal while the draft is `proposed`/`under_review`. A concurrent edit
// comes back as a 409 — the caller (useSupplierDraftActions) surfaces
// SupplierDraftApiError.httpStatus so the review page can show "This draft
// changed — reload and re-apply your edits" while preserving entered values.
export const patchSupplierDraft = async (draftId, body) => {
  const payload = updateSupplierFactorDraftRequestV1Schema.parse({ version: 1, ...body });
  return patchAndParse(
    SUPPLIER_DRAFT_ENDPOINTS.patch(draftId),
    payload,
    supplierFactorDraftResponseV1Schema,
  );
};

export const discardSupplierDraft = (draftId) =>
  postAndParse(
    SUPPLIER_DRAFT_ENDPOINTS.discard(draftId),
    supplierFactorDraftCommandRequestV1Schema.parse({ version: 1 }),
    supplierFactorDraftResponseV1Schema,
  );

// Confirm publishes the draft as a new factor revision — the response is the
// SAME shape as POST /supplier/factors/publish (revision + propagation), so it
// parses with the existing S5 contract, not a draft schema. Re-confirming an
// already-published draft is idempotent (the backend returns the same
// revision) and is treated as success by the caller.
export const confirmSupplierDraft = (draftId) =>
  postAndParse(
    SUPPLIER_DRAFT_ENDPOINTS.confirm(draftId),
    supplierFactorDraftCommandRequestV1Schema.parse({ version: 1 }),
    publishSupplierFactorResponseV1Schema,
  );
